import { ChangeDetectionStrategy, Component, computed, ViewChild } from '@angular/core';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { HeaderComponent } from '@core/components/header/header.component';
import { EventType, Router, RouterOutlet } from '@angular/router';
import { FooterComponent } from '@core/components/footer/footer.component';
import { IconComponent } from '@shared/ui-components/icon/icon.component';
import { NavLinksComponent } from '@core/components/header/nav-links/nav-links.component';
import { LangSelectionComponent } from '@core/components/header/lang-selection/lang-selection.component';
import { FloatingAlertsContainerComponent } from '@shared/components/floating-alerts/floating-alerts-container.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounce, debounceTime, distinct, filter, timer } from 'rxjs';
import { SideNavInfoDataComponent } from './ui/side-nav-info-data/side-nav-info-data.component';
import { LoadingPanelComponent } from '@shared/ui-components/loading-panel/loading-panel.component';
import { Store } from '@ngrx/store';
import { StoreState } from '@store/store.state';
import { appSelectors } from '@store/selectors/app.selectors';
import { userSelectors } from '@store/selectors/user.selectors';
import { InfoBoxComponent } from '@shared/ui-components/info-box/info-box.component';
import { TranslateModule } from '@ngx-translate/core';
import { featureFlags } from '@shared/constants/feature-flags';
import { masterSelectors } from '@store/selectors/master.selectors';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'ac-shell',
  standalone: true,
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  imports: [
    MatSidenavModule,
    SideNavInfoDataComponent,
    HeaderComponent,
    RouterOutlet,
    FooterComponent,
    IconComponent,
    NavLinksComponent,
    LangSelectionComponent,
    FloatingAlertsContainerComponent,
    LoadingPanelComponent,
    InfoBoxComponent,
    TranslateModule,
    AsyncPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellComponent {
  @ViewChild(MatSidenav) matSideNav?: MatSidenav;
  protected readonly _loading$ = this._store.select(appSelectors.generalLoading).pipe(debounceTime(50));
  protected readonly _errorUserSig = this._store.selectSignal(userSelectors.authUserError);
  protected readonly _errorSocietiesSig = this._store.selectSignal(masterSelectors.societiesError);
  protected readonly _errorInstallationsSig = this._store.selectSignal(masterSelectors.installationsError);
  protected readonly _errorSig = computed(() => this._errorUserSig() ?? this._errorSocietiesSig() ?? this._errorInstallationsSig());
  protected readonly _webIsReady$ = this._store.select(userSelectors.webIsReady).pipe(
    distinct(),
    debounce((value) => {
      if (value) {
        return timer(160);
      }
      return timer(0);
    })
  );

  constructor(
    private _router: Router,
    private _store: Store<StoreState>
  ) {
    this._router.events
      .pipe(
        filter((value) => value.type === EventType.NavigationEnd && !!this.matSideNav?.opened),
        takeUntilDestroyed()
      )
      .subscribe(() => this.matSideNav?.close());
  }
  protected readonly _shouldRenderLangSelection: boolean = featureFlags.translationFeatureFlag;
}
